import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl, fetchJson } from '../apiBase';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';

const actionSx = {
  minHeight: 44, minWidth: 44,
  '&.MuiButton-outlined:not(.Mui-disabled):hover': { color: 'primary.dark' },
  '&.Mui-focusVisible': { outline: '3px solid', outlineColor: 'text.primary', outlineOffset: 2 },
};

export default function SuperAdminPage() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [me, setMe] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [usageBySchool, setUsageBySchool] = useState({});
  const [schoolsLoaded, setSchoolsLoaded] = useState(false);
  const [schoolsError, setSchoolsError] = useState('');
  const schoolsRequest = useRef(null);
  const usageRequest = useRef(false);
  const [usageLoading, setUsageLoading] = useState(null);
  const [usageErrors, setUsageErrors] = useState({});
  const [demoLeads, setDemoLeads] = useState([]);
  const [demoLeadsLoading, setDemoLeadsLoading] = useState(false);
  const [demoLeadsError, setDemoLeadsError] = useState('');

  const loadMe = useCallback(async () => {
    try {
      const res = await fetchJson('/superadmin/me');
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.role === 'superadmin') {
        setMe(data);
      }
    } catch (_) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  async function onLogin(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const form = new URLSearchParams();
      form.set('phone', phone);
      form.set('password', password);
      const res = await fetch(apiUrl('/superadmin/login'), {
        method: 'POST',
        body: form,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
      });
      if (!res.ok) {
        const msg = (await res.text()) || 'Giriş başarısız';
        setError(msg);
        setSubmitting(false);
        return;
      }
      await loadMe();
    } catch (err) {
      setError('Giriş sırasında bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  }

  const loadSchools = useCallback(() => {
    if (schoolsRequest.current) return schoolsRequest.current;
    setSchoolsLoading(true);
    setSchoolsError('');
    const pending = (async () => {
      try {
        const res = await fetchJson('/superadmin/schools', { credentials: 'include' });
        if (!res.ok) throw new Error('schools');
        const data = await res.json();
        setSchools(Array.isArray(data.schools) ? data.schools : []);
        setSchoolsLoaded(true);
      } catch (_) {
        setSchoolsError('Okullar yüklenemedi. Tekrar deneyin.');
      } finally {
        setSchoolsLoading(false);
        schoolsRequest.current = null;
      }
    })();
    schoolsRequest.current = pending;
    return pending;
  }, []);

  const loadUsage = useCallback(async (schoolId) => {
    if (usageRequest.current) return;
    usageRequest.current = true;
    setUsageLoading(schoolId);
    setUsageErrors((prev) => ({ ...prev, [schoolId]: '' }));
    // A failed refresh must not leave old totals looking current.
    setUsageBySchool((prev) => ({ ...prev, [schoolId]: undefined }));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetchJson(`/admin/messages/usage/${schoolId}?days=21`, {
        credentials: 'include', signal: controller.signal, dedupe: false,
      });
      if (!res.ok) throw new Error('usage');
      const data = await res.json();
      setUsageBySchool((prev) => ({ ...prev, [schoolId]: data }));
    } catch (_) {
      setUsageErrors((prev) => ({ ...prev, [schoolId]: 'İstatistikler yüklenemedi. Biraz sonra tekrar deneyin.' }));
    } finally {
      clearTimeout(timeout);
      usageRequest.current = false;
      setUsageLoading(null);
    }
  }, []);

  const loadDemoLeads = useCallback(async () => {
    setDemoLeadsError('');
    setDemoLeadsLoading(true);
    try {
      const res = await fetchJson('/superadmin/demo-leads?limit=50', { credentials: 'include', dedupe: false });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDemoLeads(Array.isArray(data?.items) ? data.items : []);
    } catch (_) {
      setDemoLeadsError('Demo talepleri yüklenemedi.');
    } finally {
      setDemoLeadsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (me) {
      loadDemoLeads();
    }
  }, [me, loadDemoLeads]);

  async function chooseSchool(id) {
    try {
      const form = new URLSearchParams();
      form.set('school_id', id);
      const res = await fetch(apiUrl('/superadmin/switch_school'), {
        method: 'POST',
        body: form,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
      });
      if (res.ok) {
        // Navigate to admin
        window.location.href = '/admin';
      }
    } catch (_) {
      // ignore
    }
  }

  if (!me) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Süper Yönetici Girişi
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Telefon numaranız ve şifreniz ile giriş yapın.
          </Typography>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Box component="form" onSubmit={onLogin} noValidate>
            <Stack spacing={2.5}>
              <TextField
                label="Telefon"
                placeholder="9053xxxxxxx"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputProps={{ inputMode: 'tel' }}
                fullWidth
              />
              <TextField
                label="Şifre"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" disabled={submitting}>
                {submitting ? 'Giriş yapılıyor…' : 'Giriş Yap'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          Okul Seçimi
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Merhaba {me.name || ''}! Lütfen yönetmek istediğiniz okulu seçin.
        </Typography>
      </Box>
      <DemoLeadsPanel
        items={demoLeads}
        schools={schools}
        schoolsLoaded={schoolsLoaded}
        schoolsLoading={schoolsLoading}
        schoolsError={schoolsError}
        onLoadSchools={loadSchools}
        loading={demoLeadsLoading}
        error={demoLeadsError}
        onRefresh={loadDemoLeads}
      />
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          WhatsApp kullanımı
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Son 21 günde giden/gelen mesaj akışı.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Önce okulları yükleyin, ardından istediğiniz okulun istatistiklerini açın. Raporlar tek tek yüklenir.
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {schools.map((s) => (
            <Grid item xs={12} md={6} key={s.id}>
              <UsageCard school={s} usage={usageBySchool[s.id]} error={usageErrors[s.id]}
                loading={usageLoading === s.id} busy={usageLoading !== null} onLoad={() => loadUsage(s.id)} />
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={loadSchools} disabled={schoolsLoading} sx={actionSx}>
          {schoolsLoading ? 'Okullar yükleniyor…' : schoolsLoaded ? 'Okulları yenile' : 'Okulları yükle'}
        </Button>
        {schoolsError ? <Alert severity="error" sx={{ mt: 1 }}>{schoolsError}</Alert> : null}
        {schoolsLoaded && !schoolsLoading && !schoolsError && schools.length === 0 ? (
          <Typography role="status" sx={{ mt: 1 }}>Kayıtlı okul yok.</Typography>
        ) : null}
      </Box>
      <Grid container spacing={2}>
        {schoolsLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="rectangular" height={36} sx={{ mt: 2, borderRadius: 1 }} />
                </Paper>
              </Grid>
            ))
          : (schools || []).map((s) => (
              <Grid item xs={12} sm={6} md={4} key={s.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'box-shadow 200ms ease, transform 200ms ease',
                    '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {s.display_name || s.name || s.id}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {s.id}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" size="small" sx={actionSx} onClick={() => chooseSchool(s.id)}>
                      Bu okula git
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
      </Grid>
    </Container>
  );
}

function DemoLeadsPanel({ items, schools, schoolsLoaded, schoolsLoading, schoolsError, onLoadSchools, loading, error, onRefresh }) {
  const [previewLead, setPreviewLead] = useState(null);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');
  const [approveResult, setApproveResult] = useState(null);

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (_) {
      return iso;
    }
  };

  const openPreview = (item) => {
    setPreviewLead(item);
    setSelectedSchoolId('');
    setApproveError('');
    setApproveResult(null);
  };

  const closePreview = () => {
    if (approving) return;
    setPreviewLead(null);
    setSelectedSchoolId('');
    setApproveError('');
    setApproveResult(null);
  };

  const approveLead = async () => {
    if (!previewLead?.id || !selectedSchoolId) {
      setApproveError('Onaylamak için okul seçin.');
      return;
    }
    setApproving(true);
    setApproveError('');
    setApproveResult(null);
    try {
      const res = await fetchJson(`/superadmin/demo-leads/${previewLead.id}/approve`, {
        method: 'POST',
        credentials: 'include',
        dedupe: false,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ school_id: selectedSchoolId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setApproveError(data?.detail || 'Demo talebi onaylanamadı.');
        return;
      }
      setApproveResult(data?.result || {});
      await onRefresh?.();
    } catch (_) {
      setApproveError('Onay sırasında bağlantı hatası oluştu.');
    } finally {
      setApproving(false);
    }
  };

  const previewPayload = previewLead?.payload || {};
  const previewTeacher = previewPayload?.teacher || {};
  const previewClassroom = previewPayload?.classroom || {};
  const previewStudents = Array.isArray(previewPayload?.students) ? previewPayload.students : [];

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Demo Talepleri
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Homepage kurulum formundan gelen geçici kayıtlar. Buradan kontrol edip öğretmenle iletişime geçebilirsiniz.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={onRefresh} disabled={loading}>
          Yenile
        </Button>
      </Box>

      {error ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        {loading && (!items || items.length === 0)
          ? Array.from({ length: 2 }).map((_, i) => (
              <Grid item xs={12} md={6} key={`demo-lead-skel-${i}`}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Skeleton variant="text" width="50%" height={28} />
                  <Skeleton variant="text" width="35%" />
                  <Skeleton variant="rectangular" height={84} sx={{ mt: 1.5, borderRadius: 1 }} />
                </Paper>
              </Grid>
            ))
          : null}

        {!loading && (!items || items.length === 0) ? (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Henüz demo talebi yok.
              </Typography>
            </Paper>
          </Grid>
        ) : null}

        {(items || []).map((item) => {
          const teacherName = [item.teacher_first_name, item.teacher_last_name].filter(Boolean).join(' ') || 'İsimsiz öğretmen';
          const students = Array.isArray(item?.payload?.students) ? item.payload.students : [];
          const firstStudent = students[0];
          return (
            <Grid item xs={12} md={6} key={item.id}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                }}
              >
                <Stack spacing={1.25}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {teacherName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        #{item.id} • {formatDateTime(item.created_at)}
                      </Typography>
                    </Box>
                    <Chip label={item.status || 'new'} size="small" color="primary" variant="outlined" />
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Chip label={`Tel: ${item.teacher_phone || '-'}`} size="small" />
                    <Chip label={`Sınıf: ${item.class_name || '-'}`} size="small" />
                    <Chip label={`${item.student_count || 0} öğrenci`} size="small" />
                  </Stack>

                  <Divider />

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.branch || 'Branş yok'} • {item.grade_level || 'Kademe yok'} • {item.education_year || 'Eğitim yılı yok'}
                    </Typography>
                    {firstStudent ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        İlk kayıt: {[firstStudent.student_first_name, firstStudent.student_last_name].filter(Boolean).join(' ') || 'Öğrenci adı yok'}
                        {' / '}
                        {[firstStudent.parent_first_name, firstStudent.parent_last_name].filter(Boolean).join(' ') || 'Veli adı yok'}
                        {firstStudent.parent_phone ? ` (${firstStudent.parent_phone})` : ''}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Öğrenci-veli kaydı girilmemiş.
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                  <Button size="small" variant="outlined" onClick={() => openPreview(item)}>
                    Önizle
                  </Button>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={Boolean(previewLead)} onClose={closePreview} fullWidth maxWidth="md">
        <DialogTitle>Demo Talebi Önizleme</DialogTitle>
        <DialogContent dividers>
          {previewLead ? (
            <Stack spacing={2.5}>
              {approveError ? <Alert severity="error">{approveError}</Alert> : null}
              {approveResult ? (
                <Alert severity="success">
                  Onaylandı. Öğretmen: {approveResult?.summary?.created_teachers || 0} yeni,
                  sınıf: {approveResult?.summary?.created_classes || 0} yeni,
                  veli: {approveResult?.summary?.created_parents || 0} yeni,
                  öğrenci: {approveResult?.summary?.created_students || 0} yeni.
                </Alert>
              ) : null}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Öğretmen
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, mt: 1 }}>
                      {[previewTeacher.first_name || previewLead.teacher_first_name, previewTeacher.last_name || previewLead.teacher_last_name].filter(Boolean).join(' ') || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {previewTeacher.phone || previewLead.teacher_phone || '-'} • {previewTeacher.branch || previewLead.branch || '-'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Sınıf
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, mt: 1 }}>
                      {previewClassroom.name || previewLead.class_name || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {previewClassroom.grade_level || previewLead.grade_level || 'Kademe yok'} • {previewClassroom.education_year || previewLead.education_year || 'Eğitim yılı yok'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Öğrenci ve Veli Kayıtları
                </Typography>
                <Stack spacing={1}>
                  {previewStudents.length ? previewStudents.map((student, index) => (
                    <Paper key={`${student.student_first_name || 'student'}-${index}`} elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {[student.student_first_name, student.student_last_name].filter(Boolean).join(' ') || `Öğrenci ${index + 1}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Veli: {[student.parent_first_name, student.parent_last_name].filter(Boolean).join(' ') || '-'}
                        {student.parent_phone ? ` • ${student.parent_phone}` : ''}
                        {student.student_no ? ` • No: ${student.student_no}` : ''}
                      </Typography>
                    </Paper>
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      Öğrenci-veli kaydı yok.
                    </Typography>
                  )}
                </Stack>
              </Box>

              {!schoolsLoaded || schoolsError ? (
                <Box>
                  <Typography sx={{ mb: 1 }}>Demo talebini onaylamak için okul listesini yükleyin.</Typography>
                  <Button variant="outlined" sx={actionSx} onClick={onLoadSchools} disabled={schoolsLoading}>
                    {schoolsLoading ? 'Okullar yükleniyor…' : 'Okulları yükle'}
                  </Button>
                  {schoolsError ? <Alert severity="error" sx={{ mt: 1 }}>{schoolsError}</Alert> : null}
                </Box>
              ) : null}
              {schoolsLoaded && !schoolsLoading && !schoolsError && schools.length === 0 ? (
                <Typography role="status">Kayıtlı okul yok.</Typography>
              ) : null}
              <FormControl fullWidth disabled={!schoolsLoaded || schoolsLoading || Boolean(schoolsError) || previewLead.status === 'approved' || approving || Boolean(approveResult)}>
                <InputLabel id="demo-approve-school-label">Oluşturulacak okul</InputLabel>
                <Select
                  labelId="demo-approve-school-label"
                  label="Oluşturulacak okul"
                  value={selectedSchoolId}
                  onChange={(event) => setSelectedSchoolId(event.target.value)}
                >
                  {(schools || []).map((school) => (
                    <MenuItem value={String(school.id)} key={school.id}>
                      {school.display_name || school.name || school.code || school.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} disabled={approving}>
            Kapat
          </Button>
          <Button
            variant="contained"
            onClick={approveLead}
            sx={actionSx}
            disabled={!selectedSchoolId || schoolsLoading || Boolean(schoolsError) || !previewLead || previewLead.status === 'approved' || approving || Boolean(approveResult)}
          >
            {approving ? 'Onaylanıyor…' : previewLead?.status === 'approved' ? 'Onaylandı' : 'Onayla ve Kullanıcıları Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function UsageCard({ school, usage, loading, error, busy, onLoad }) {
  const theme = useTheme();
  const formatDay = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('tr-TR', { month: '2-digit', day: '2-digit' });
    } catch (_) {
      return iso;
    }
  };
  const outbound = usage?.daily_outbound || [];
  const inbound = usage?.daily_inbound || [];
  const outboundAxis = outbound.map((d) => formatDay(d.date));
  const inboundAxis = inbound.map((d) => formatDay(d.date));
  const safeOutboundAxis = outboundAxis.length ? outboundAxis : ['-'];
  const safeInboundAxis = inboundAxis.length ? inboundAxis : ['-'];
  const safeOutboundData = outbound.length ? outbound.map((d) => Number(d.count || 0)) : [0];
  const safeInboundData = inbound.length ? inbound.map((d) => Number(d.count || 0)) : [0];
  const totalOut = typeof usage?.total_outbound === 'number' ? usage.total_outbound : outbound.reduce((s, d) => s + Number(d.count || 0), 0);
  const totalIn = typeof usage?.total_inbound === 'number' ? usage.total_inbound : inbound.reduce((s, d) => s + Number(d.count || 0), 0);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {school.display_name || school.name || school.id}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {school.id}
          </Typography>
        </Box>
        {usage ? <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Giden: ${totalOut}`} size="small" color="primary" variant="outlined" />
          <Chip label={`Gelen: ${totalIn}`} size="small" color="success" variant="outlined" />
        </Stack> : null}
      </Box>

      <Button variant="outlined" sx={actionSx} onClick={onLoad} disabled={busy}
        aria-label={`${school.display_name || school.name || school.id}: istatistikleri ${usage ? 'yenile' : 'yükle'}`}>
        {loading ? 'İstatistikler yükleniyor…' : usage ? 'İstatistikleri yenile' : 'İstatistikleri yükle'}
      </Button>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? <Typography role="status">İstatistikler yükleniyor…</Typography> : null}
      {loading && !usage ? (
        <Box>
          <Skeleton variant="text" width="30%" height={20} />
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="text" width="30%" height={20} />
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1, mb: 2 }} />
        </Box>
      ) : usage ? (
        <>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Günlük giden
            </Typography>
            <LineChart
              height={150}
              slotProps={{ legend: { hidden: true } }}
              xAxis={[{
                scaleType: 'point',
                data: safeOutboundAxis,
                tickLabelStyle: { fontSize: 11 },
              }]}
              series={[{
                data: safeOutboundData,
                color: theme.palette.primary.main,
                curve: 'monotoneX',
                area: true,
              }]}
              margin={{ top: 10, right: 10, bottom: 30, left: 35 }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Günlük gelen
            </Typography>
            <LineChart
              height={150}
              slotProps={{ legend: { hidden: true } }}
              xAxis={[{
                scaleType: 'point',
                data: safeInboundAxis,
                tickLabelStyle: { fontSize: 11 },
              }]}
              series={[{
                data: safeInboundData,
                color: theme.palette.success.main,
                curve: 'monotoneX',
                area: true,
              }]}
              margin={{ top: 10, right: 10, bottom: 30, left: 35 }}
            />
          </Box>
        </>
      ) : !error ? (
        <Typography variant="body2" color="text.secondary">
          İstatistikler henüz yüklenmedi.
        </Typography>
      ) : null}
    </Paper>
  );
}
